// You'll need a decimal library like decimal.js for accurate decimal arithmetic.
// Example: npm install decimal.js
import BigNumber from 'bignumber.js'
import crypto from 'crypto' // Node.js built-in crypto module
import { WheelGameRiskEnum } from 'views/InhouseGame/Wheel/config'
import { WheelSegments } from 'views/InhouseGame/Wheel/config/types'
import { calProbFromHash, HASH_HEX_LENGTH } from './baseFairnessResult'

const wheelData = {
  SegmentsIndex: {
    10: 0,
    20: 1,
    30: 2,
    40: 3,
    50: 4,
  },
  PayoutValues: {
    // Corresponds to SegmentsIndex: 10 (index 0)
    10: [
      [0, 1.2, 1.5], // Risk 0
      [0, 1.5, 1.9, 2.0, 3.0], // Risk 1
      [0, 9.9], // Risk 2
    ],
    // Corresponds to SegmentsIndex: 20 (index 1)
    20: [
      [0, 1.2, 1.5],
      [0, 1.5, 1.8, 2.0, 3.0],
      [0, 19.8],
    ],
    // Corresponds to SegmentsIndex: 30 (index 2)
    30: [
      [0, 1.2, 1.5],
      [0, 1.5, 1.7, 2.0, 3.0, 4.0],
      [0, 29.7],
    ],
    // Corresponds to SegmentsIndex: 40 (index 3)
    40: [
      [0, 1.2, 1.5],
      [0, 1.5, 1.6, 2.0, 3.0],
      [0, 39.6],
    ],
    // Corresponds to SegmentsIndex: 50 (index 4)
    50: [
      [0, 1.2, 1.5],
      [0, 1.5, 2.0, 3.0, 5.0],
      [0, 49.5],
    ],
  },
  // x hit if x in [a, b)
  PascalTriangle: [
    // Corresponds to SegmentsIndex: 10 (index 0)
    [
      [2, 9, 10], // Risk 0
      [5, 7, 8, 9, 10], // Risk 1
      [9, 10], // Risk 2
    ],
    // Corresponds to SegmentsIndex: 20 (index 1)
    [
      [4, 18, 20],
      [10, 12, 13, 19, 20],
      [19, 20],
    ],
    // Corresponds to SegmentsIndex: 30 (index 2)
    [
      [6, 27, 30],
      [15, 21, 22, 28, 29, 30],
      [29, 30],
    ],
    // Corresponds to SegmentsIndex: 40 (index 3)
    [
      [8, 36, 40],
      [20, 28, 29, 36, 40],
      [39, 40],
    ],
    // Corresponds to SegmentsIndex: 50 (index 4)
    [
      [10, 45, 50],
      [25, 38, 46, 49, 50],
      [49, 50],
    ],
  ],
}

export function generateWheelFairResult(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  segments: WheelSegments,
  risk: WheelGameRiskEnum,
) {
  const hash = generateHash(serverSeed, clientSeed, nonce, segments, risk)

  // prob is in [0, 1)
  const prob = calProbFromHash(hash, HASH_HEX_LENGTH)

  // Convert prob from [0, 1) to [0, segments) using decimal.js
  const dec = new BigNumber(prob).multipliedBy(segments)

  // Get the segmentIndex from wheelData.SegmentsIndex based on the number of segments
  const segmentIndex = wheelData.SegmentsIndex[segments]

  const outcomeIndex = calculateOutcomeData(dec, segmentIndex, risk)
  return wheelData.PayoutValues[segments][risk][outcomeIndex]
}

function generateHash(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  segments: WheelSegments,
  risk: WheelGameRiskEnum,
) {
  const key = serverSeed
  const message = `${clientSeed}:${nonce}:${segments}:${risk}` // Corrected rowCount to segments
  const hmac = crypto.createHmac('sha256', key)
  hmac.update(message)
  return hmac.digest('hex')
}

function calculateOutcomeData(dec: BigNumber, segmentIndex: number, risk: WheelGameRiskEnum) {
  // Access the correct Pascal's Triangle row based on segmentIndex and risk
  const pascalTriangleRow = wheelData.PascalTriangle[segmentIndex][risk]

  // Binary search to find the closest value to dec
  let index = 0
  let low = 0
  let high = pascalTriangleRow.length - 1

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    // Compare using decimal.js's greaterThan method
    if (new BigNumber(pascalTriangleRow[mid]).gt(dec)) {
      index = mid
      high = mid - 1
    } else {
      low = mid + 1
    }
  }

  // Check if the found index is out of bounds (original Go logic handles this by decrementing if it's the length)
  if (index === pascalTriangleRow.length && pascalTriangleRow.length > 0) {
    index--
  }

  return index
}
